/**
 * getIconColorFromSummary.js
 *
 * Pure utility that computes icon color, background, and border
 * for a ReferenceMarker based on its claims summary.
 *
 * - Green: all claims proven
 * - Red: any contradicted, unproven, or partially proven claims
 * - Blue: no claims or no analysis yet
 */

/**
 * @param {Object|null} summary  Claims summary with snake_case fields
 * @param {{ color: string, bg: string, border: string }} defaultCfg  Fallback from markerTheme
 * @param {{ contradictionCount?: number }} [options]
 * @returns {{ iconColor: string, iconBg: string, iconBorder: string }}
 */
export function getIconColorFromSummary(summary, defaultCfg = {}, options = {}) {
    const contradictionCount = options.contradictionCount ?? 0;

    if (contradictionCount > 0) {
        return {
            iconColor: '#d32f2f',
            iconBg: 'rgba(211, 47, 47, 0.14)',
            iconBorder: 'rgba(211, 47, 47, 0.55)',
        };
    }

    // No analysis yet → blue
    if (!summary) {
        return {
            iconColor: '#1976d2',
            iconBg: 'rgba(25, 118, 210, 0.14)',
            iconBorder: 'rgba(25, 118, 210, 0.55)',
        };
    }

    const {contradicted_claims, unproven_claims, partially_proven_claims, proven_claims, total_claims} = summary;

    if (total_claims === 0) {
        // Blue: no claims in the statement
        return {
            iconColor: '#1976d2',
            iconBg: 'rgba(25, 118, 210, 0.14)',
            iconBorder: 'rgba(25, 118, 210, 0.55)',
        };
    }

    if (contradicted_claims > 0 || unproven_claims > 0 || partially_proven_claims > 0) {
        // Red: one or more claims not fully proven
        return {
            iconColor: '#d32f2f',
            iconBg: 'rgba(211, 47, 47, 0.14)',
            iconBorder: 'rgba(211, 47, 47, 0.55)',
        };
    }

    if (proven_claims === total_claims && total_claims > 0) {
        // Green: all claims proven
        return {
            iconColor: '#2e7d32',
            iconBg: 'rgba(46, 125, 50, 0.14)',
            iconBorder: 'rgba(46, 125, 50, 0.55)',
        };
    }

    // Fallback to defaults (shouldn't happen, but defensive)
    return {
        iconColor: defaultCfg.color || '#1976d2',
        iconBg: defaultCfg.bg || 'rgba(25, 118, 210, 0.14)',
        iconBorder: defaultCfg.border || 'rgba(25, 118, 210, 0.55)',
    };
}

